// src/components/ModalFormClienteRecepcion.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";

import { putClienteRecepcion as modifClienteRecepcion } from "../helpers/rutaClienterecepcion";

/* ---------------- utils ---------------- */

const clean = (v) => String(v ?? "").trim();

const buildNombreCompleto = ({
  nombre,
  segundoNombre,
  apellido1,
  apellido2,
  nombreCompleto,
}) => {
  const armado = [
    clean(nombre),
    clean(segundoNombre),
    clean(apellido1),
    clean(apellido2),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(nombreCompleto);
};

const buildDireccionCompleta = ({
  direccion,
  referenciaDireccion,
  municipio,
  provincia,
  pais,
  direccionCompleta,
}) => {
  const armado = [
    clean(direccion),
    clean(referenciaDireccion),
    clean(municipio),
    clean(provincia),
    clean(pais) || "Cuba",
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(direccionCompleta);
};

const splitNombreLegacy = (cliente = {}) => {
  const nombre = clean(cliente?.nombre);
  const segundoNombre = clean(cliente?.segundoNombre);
  const apellido1 = clean(cliente?.apellido1);
  const apellido2 = clean(cliente?.apellido2);

  if (nombre || segundoNombre || apellido1 || apellido2) {
    return {
      nombre,
      segundoNombre,
      apellido1,
      apellido2,
    };
  }

  const completo = clean(cliente?.nombreCompleto);

  if (!completo) {
    return {
      nombre: "",
      segundoNombre: "",
      apellido1: "",
      apellido2: "",
    };
  }

  const partes = completo.split(/\s+/).filter(Boolean);

  return {
    nombre: partes[0] || "",
    segundoNombre: partes.length >= 4 ? partes[1] || "" : "",
    apellido1:
      partes.length >= 4
        ? partes[2] || ""
        : partes.length >= 2
        ? partes[1] || ""
        : "",
    apellido2:
      partes.length >= 4
        ? partes.slice(3).join(" ")
        : partes.length >= 3
        ? partes.slice(2).join(" ")
        : "",
  };
};

const initial = {
  codrecep: "",

  nombre: "",
  segundoNombre: "",
  apellido1: "",
  apellido2: "",
  nombreCompleto: "",

  carnetIdentidad: "",

  telefonoPrincipal: "",
  email: "",

  direccion: "",
  referenciaDireccion: "",
  provincia: "",
  municipio: "",
  pais: "Cuba",
  direccionCompleta: "",
};

/* ---------------- Provincias Cuba ---------------- */

const CUBA_PROVINCIAS = [
  "Pinar del Río",
  "Artemisa",
  "La Habana",
  "Mayabeque",
  "Matanzas",
  "Cienfuegos",
  "Villa Clara",
  "Sancti Spíritus",
  "Ciego de Ávila",
  "Camagüey",
  "Las Tunas",
  "Holguín",
  "Granma",
  "Santiago de Cuba",
  "Guantánamo",
  "Isla de la Juventud",
];

export default function ModalFormClienteRecepcion({
  cliente,
  handleClose,
  show,
  onSaved,
}) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ---------------- modal provincia ---------------- */

  const [showProvinciaModal, setShowProvinciaModal] = useState(false);
  const [qProvincia, setQProvincia] = useState("");
  const [selIdxProvincia, setSelIdxProvincia] = useState(0);
  const inputProvinciaRef = useRef(null);

  const provinciaOpts = useMemo(() => {
    const q = clean(qProvincia).toLowerCase();

    if (!q) return CUBA_PROVINCIAS;

    return CUBA_PROVINCIAS.filter((provincia) =>
      provincia.toLowerCase().includes(q)
    );
  }, [qProvincia]);

  const closeProvinciaModal = useCallback(() => {
    setShowProvinciaModal(false);
  }, []);

  const openProvinciaModal = useCallback(() => {
    setShowProvinciaModal(true);
    setQProvincia("");
    setSelIdxProvincia(0);

    setTimeout(() => inputProvinciaRef.current?.focus(), 50);
  }, []);

  const seleccionarProvincia = useCallback(
    (provincia) => {
      if (!provincia) return;

      setFormValues((prev) => ({
        ...prev,
        provincia,
      }));

      closeProvinciaModal();
    },
    [closeProvinciaModal]
  );

  const onProvinciaModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeProvinciaModal();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setSelIdxProvincia((i) =>
        Math.min(i + 1, Math.max(0, provinciaOpts.length - 1))
      );

      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setSelIdxProvincia((i) => Math.max(i - 1, 0));

      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      const pick = provinciaOpts[selIdxProvincia] || provinciaOpts[0];

      if (pick) seleccionarProvincia(pick);
    }
  };

  /* ---------------- form helpers ---------------- */

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const nombreCompleto = useMemo(
    () => buildNombreCompleto(formValues),
    [
      formValues.nombre,
      formValues.segundoNombre,
      formValues.apellido1,
      formValues.apellido2,
      formValues.nombreCompleto,
    ]
  );

  const direccionCompleta = useMemo(
    () => buildDireccionCompleta(formValues),
    [
      formValues.direccion,
      formValues.referenciaDireccion,
      formValues.municipio,
      formValues.provincia,
      formValues.pais,
      formValues.direccionCompleta,
    ]
  );

  /* ---------------- hidratar cliente ---------------- */

  useEffect(() => {
    if (!show || !cliente) return;

    const split = splitNombreLegacy(cliente);

    const next = {
      codrecep: clean(cliente?.codrecep),

      nombre: clean(cliente?.nombre) || split.nombre,
      segundoNombre: clean(cliente?.segundoNombre) || split.segundoNombre,
      apellido1: clean(cliente?.apellido1) || split.apellido1,
      apellido2: clean(cliente?.apellido2) || split.apellido2,
      nombreCompleto: clean(cliente?.nombreCompleto),

      carnetIdentidad: clean(
        cliente?.carnetIdentidad ??
          cliente?.carnet ??
          cliente?.ci ??
          cliente?.documento ??
          cliente?.idDocumento
      ),

      telefonoPrincipal: clean(
        cliente?.telefonoPrincipal ??
          cliente?.telefono ??
          cliente?.mobile ??
          cliente?.celular ??
          cliente?.celular1
      ),

      email: clean(cliente?.email ?? cliente?.correo),

      direccion: clean(cliente?.direccion ?? cliente?.addressLine1),
      referenciaDireccion: clean(
        cliente?.referenciaDireccion ??
          cliente?.referencia ??
          cliente?.addressLine2
      ),
      provincia: clean(cliente?.provincia),
      municipio: clean(cliente?.municipio),
      pais: clean(cliente?.pais ?? cliente?.country) || "Cuba",
      direccionCompleta: clean(cliente?.direccionCompleta),
    };

    setFormValues(next);
    setQProvincia("");
    setSelIdxProvincia(0);
  }, [cliente, show]);

  /* ---------------- validate / payload ---------------- */

  const validate = () => {
    if (!clean(formValues.carnetIdentidad)) {
      return "Ingresá el Carnet de identidad.";
    }

    if (!clean(formValues.nombre)) {
      return "Ingresá el Nombre.";
    }

    if (!clean(formValues.apellido1)) {
      return "Ingresá el Apellido 1.";
    }

    if (!clean(formValues.telefonoPrincipal)) {
      return "Ingresá el Teléfono principal.";
    }

    if (!clean(formValues.direccion)) {
      return "Ingresá la Dirección.";
    }

    if (!clean(formValues.provincia)) {
      return "Ingresá la Provincia.";
    }

    if (!clean(formValues.municipio)) {
      return "Ingresá el Municipio.";
    }

    const email = clean(formValues.email);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Ingresá un correo electrónico válido.";
    }

    return "";
  };

  const buildPayload = () => {
    const nombre = clean(formValues.nombre);
    const segundoNombre = clean(formValues.segundoNombre);
    const apellido1 = clean(formValues.apellido1);
    const apellido2 = clean(formValues.apellido2);

    const direccion = clean(formValues.direccion);
    const referenciaDireccion = clean(formValues.referenciaDireccion);
    const provincia = clean(formValues.provincia);
    const municipio = clean(formValues.municipio);
    const pais = clean(formValues.pais) || "Cuba";

    const payload = {
      codrecep: clean(formValues.codrecep),

      nombre,
      segundoNombre,
      apellido1,
      apellido2,
      nombreCompleto: buildNombreCompleto({
        nombre,
        segundoNombre,
        apellido1,
        apellido2,
        nombreCompleto: formValues.nombreCompleto,
      }),

      carnetIdentidad: clean(formValues.carnetIdentidad),

      telefonoPrincipal: clean(formValues.telefonoPrincipal),
      email: clean(formValues.email),

      direccion,
      referenciaDireccion,
      provincia,
      municipio,
      pais,
      direccionCompleta: buildDireccionCompleta({
        direccion,
        referenciaDireccion,
        municipio,
        provincia,
        pais,
        direccionCompleta: formValues.direccionCompleta,
      }),

      activo: true,
    };

    if (!clean(payload.codrecep)) {
      delete payload.codrecep;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();

    if (msg) return alert(msg);

    const clienteId = cliente?._id || cliente?.id;

    if (!clienteId) {
      return alert("No se encontró el ID del cliente de recepción a modificar.");
    }

    const payload = buildPayload();

    try {
      setSaving(true);

      const resp = await modifClienteRecepcion(clienteId, payload);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);

        if (typeof onSaved === "function") {
          onSaved(resp);
        }

        handleClose();
      }, 900);
    } catch (error) {
      console.error(
        "❌ Error al actualizar cliente recepción:",
        error?.response?.data || error
      );

      const mensaje =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudo actualizar el cliente de recepción.";

      alert(mensaje);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        scrollable
      >
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Cliente Receptor</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="container-fluid">
              {/* ---------------- fila 1 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-2">
                  <label>Código</label>
                  <input
                    className="form-control"
                    name="codrecep"
                    value={formValues.codrecep}
                    disabled
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Carnet de identidad *</label>
                  <input
                    className="form-control"
                    name="carnetIdentidad"
                    value={formValues.carnetIdentidad}
                    onChange={handleChange}
                    placeholder="Carnet de identidad"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Teléfono Principal *</label>
                  <input
                    className="form-control"
                    name="telefonoPrincipal"
                    value={formValues.telefonoPrincipal}
                    onChange={handleChange}
                    placeholder="Teléfono principal"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Correo electrónico</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    placeholder="correo@email.com"
                  />
                </div>
              </div>

              {/* ---------------- fila 2 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-3">
                  <label>Nombre *</label>
                  <input
                    className="form-control"
                    name="nombre"
                    value={formValues.nombre}
                    onChange={handleChange}
                    placeholder="First Name"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Segundo nombre</label>
                  <input
                    className="form-control"
                    name="segundoNombre"
                    value={formValues.segundoNombre}
                    onChange={handleChange}
                    placeholder="Middle Name"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Apellido 1 *</label>
                  <input
                    className="form-control"
                    name="apellido1"
                    value={formValues.apellido1}
                    onChange={handleChange}
                    placeholder="Last Name"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Apellido 2</label>
                  <input
                    className="form-control"
                    name="apellido2"
                    value={formValues.apellido2}
                    onChange={handleChange}
                    placeholder="Segundo apellido"
                  />
                </div>
              </div>

              {/* ---------------- nombre completo ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-12">
                  <label>Nombre completo</label>
                  <input
                    className="form-control"
                    value={nombreCompleto}
                    readOnly
                    placeholder="Se arma automáticamente"
                  />
                  <small className="text-muted">
                    Se guarda como <b>nombreCompleto</b> para búsquedas e informes.
                  </small>
                </div>
              </div>

              {/* ---------------- dirección ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-7">
                  <label>Dirección *</label>
                  <input
                    className="form-control"
                    name="direccion"
                    value={formValues.direccion}
                    onChange={handleChange}
                    placeholder="Calle, número, casa, apto, edificio, reparto/barrio"
                  />
                  <small className="text-muted">
                    Ej: Calle 10 #125, Apto 3, Reparto Vista Alegre.
                  </small>
                </div>

                <div className="form-group mt-2 col-md-5">
                  <label>Referencia / Entre calles</label>
                  <input
                    className="form-control"
                    name="referenciaDireccion"
                    value={formValues.referenciaDireccion}
                    onChange={handleChange}
                    placeholder="Entre calles, cerca de, color de casa, referencia"
                  />
                  <small className="text-muted">
                    Muy útil para entregar sin perderse.
                  </small>
                </div>
              </div>

              {/* ---------------- provincia / municipio / país ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-4">
                  <label>Provincia *</label>

                  <div className="input-group">
                    <input
                      className="form-control"
                      name="provincia"
                      value={formValues.provincia}
                      onChange={handleChange}
                      placeholder="Provincia"
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={openProvinciaModal}
                      title="Buscar provincia"
                      style={{
                        width: 44,
                        height: 38,
                        padding: 0,
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      🔎
                    </button>
                  </div>

                  <small className="text-muted">
                    Podés escribirla o buscarla.
                  </small>
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Municipio *</label>
                  <input
                    className="form-control"
                    name="municipio"
                    value={formValues.municipio}
                    onChange={handleChange}
                    placeholder="Municipio"
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>País</label>
                  <input
                    className="form-control"
                    name="pais"
                    value={formValues.pais}
                    onChange={handleChange}
                    placeholder="Cuba"
                  />
                </div>
              </div>

              {/* ---------------- dirección completa ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-12">
                  <label>Dirección completa</label>
                  <input
                    className="form-control"
                    value={direccionCompleta}
                    readOnly
                    placeholder="Se arma automáticamente"
                  />
                  <small className="text-muted">
                    Esta dirección se guarda para búsquedas, informes y logística.
                  </small>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>

            <Button type="submit" variant="dark" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" /> Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ---------------- Modal buscar Provincia ---------------- */}
      <Modal
        show={showProvinciaModal}
        onHide={closeProvinciaModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar provincia</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Provincia</Form.Label>
            <Form.Control
              ref={inputProvinciaRef}
              value={qProvincia}
              placeholder="Ej: La Habana"
              onChange={(e) => {
                setQProvincia(e.target.value);
                setSelIdxProvincia(0);
              }}
              onKeyDown={onProvinciaModalKeyDown}
            />

            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!provinciaOpts.length && (
              <div className="text-muted">Sin resultados.</div>
            )}

            {!!provinciaOpts.length && (
              <ListGroup>
                {provinciaOpts.map((provincia, idx) => (
                  <ListGroup.Item
                    key={provincia}
                    action
                    active={idx === selIdxProvincia}
                    onClick={() => seleccionarProvincia(provincia)}
                  >
                    {provincia}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeProvinciaModal}>
            Cerrar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              seleccionarProvincia(
                provinciaOpts[selIdxProvincia] || provinciaOpts[0]
              )
            }
            disabled={!provinciaOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------------- Modal éxito ---------------- */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}